/**
 * Skin pattern overlays — render the signature visual style on top of
 * a snake's base body.
 *
 * Extracted from SnakeRenderer so both renderers can share the same
 * code: SnakeRenderer (used for the local predicted player in live
 * mode + every snake in offline mode) and LiveWorldRenderer (used
 * for every remote snake in live mode). Before extraction, the live
 * renderer skipped pattern overlays entirely — every remote snake
 * looked like a flat default body even though the server assigns
 * each one a real pattern, which is why other snakes "looked weird"
 * in live mode.
 *
 * The overlays don't need a full Snake object — just three things:
 *   - hue:     base color
 *   - idSeed:  deterministic integer seed for noise (per-snake)
 *   - widthAt: function that returns the body radius at progress
 *              u in [0,1] along the snake's length
 * Both renderers build a minimal `PatternOpts` and call here. Keeps
 * the overlays pure and renderer-agnostic.
 */
import type { SkinPattern } from '../entities/Snake';

export interface PatternOpts {
  hue: number;
  /** Deterministic integer per-snake — used as the noise seed so the
   *  same snake renders with a stable chip/crack/vein pattern across
   *  frames. For local Snake objects this is `snake.id` (numeric);
   *  for remote ServerPlayer entries the string sessionId is hashed
   *  via {@link hashSessionId}. */
  idSeed: number;
  /** Body radius at progress u (head=0, tail=1). Mirrors
   *  Snake.widthAt — already includes head-to-tail taper. */
  widthAt: (u: number) => number;
}

/** FNV-1a 32-bit hash. Used to map a Colyseus string sessionId into
 *  the deterministic integer the overlays seed on. */
export function hashSessionId(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = (h ^ s.charCodeAt(i)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h | 0;
}

/** Dispatch on pattern — single entry point both renderers call. */
export function drawPatternOverlay(
  ctx: CanvasRenderingContext2D,
  pattern: SkinPattern | undefined,
  opts: PatternOpts,
  xs: number[],
  ys: number[],
  n: number,
  time: number,
): void {
  switch (pattern) {
    case 'glitch':    drawGlitchOverlay(ctx, opts, xs, ys, n, time); break;
    case 'plasma':    drawPlasmaOverlay(ctx, opts, xs, ys, n, time); break;
    case 'geometric': drawGeometricOverlay(ctx, opts, xs, ys, n, time); break;
    case 'bio':       drawBioOverlay(ctx, opts, xs, ys, n, time); break;
  }
}

/**
 * GLITCH — chromatic-aberration chips along the body, ticking every
 * 0.2 s. The seed mixes per-segment + per-tick so chips appear to
 * "jitter" rather than drift.
 */
function drawGlitchOverlay(
  ctx: CanvasRenderingContext2D,
  opts: PatternOpts,
  xs: number[],
  ys: number[],
  n: number,
  time: number,
): void {
  const { hue, idSeed } = opts;
  const tick = Math.floor(time * 5);
  ctx.save();
  ctx.shadowBlur = 4;
  for (let i = 1; i < n; i += 2) {
    const u = i / (n - 1);
    const r = Math.max(1.5, opts.widthAt(u));
    const seed = (i * 73856093) ^ (tick * 19349663) ^ idSeed;
    const rng1 = ((seed >>> 0) & 0xffff) / 0xffff;
    const rng2 = (((seed * 2654435769) >>> 0) & 0xffff) / 0xffff;
    const rng3 = (((seed * 40503) >>> 0) & 0xffff) / 0xffff;
    if (rng1 < 0.35) continue;
    const cx = xs[i]!;
    const cy = ys[i]!;
    const prev = i > 0 ? i - 1 : i;
    const next = i < n - 1 ? i + 1 : i;
    const dx = xs[next]! - xs[prev]!;
    const dy = ys[next]! - ys[prev]!;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    const chips = 1 + Math.floor(rng2 * 2.6);
    const pxSize = Math.max(2, r * 0.55);
    for (let k = 0; k < chips; k++) {
      const off = (rng3 + k * 0.31) * 2 - 1;
      const x = cx + px * off * r * 0.75;
      const y = cy + py * off * r * 0.75;
      const hueDrift = ((seed + k * 37) % 37) - 18;
      const chipHue = (hue + hueDrift + 360) % 360;
      const lightness = 55 + ((seed >> (k * 3)) % 35);
      ctx.fillStyle = `hsl(${chipHue} 100% ${lightness}%)`;
      ctx.shadowColor = `hsl(${chipHue} 100% ${Math.min(95, lightness + 10)}%)`;
      ctx.fillRect(x - pxSize * 0.5, y - pxSize * 0.5, pxSize, pxSize);
    }
  }
  ctx.restore();
}

/**
 * PLASMA — two flowing electric arcs along opposite sides of the body.
 * Both stay in the snake's own hue; differentiation comes from
 * lightness and opposing offset directions.
 */
function drawPlasmaOverlay(
  ctx: CanvasRenderingContext2D,
  opts: PatternOpts,
  xs: number[],
  ys: number[],
  n: number,
  time: number,
): void {
  const { hue } = opts;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const [lightness, sign, phase] of [
    [70,  1, 0.0] as const,
    [88, -1, 1.2] as const,
  ]) {
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      const r = Math.max(1.5, opts.widthAt(u));
      const prev = i > 0 ? i - 1 : i;
      const next = i < n - 1 ? i + 1 : i;
      const dx = xs[next]! - xs[prev]!;
      const dy = ys[next]! - ys[prev]!;
      const len = Math.hypot(dx, dy) || 1;
      const perpx = -dy / len;
      const perpy = dx / len;
      const arc = Math.sin(u * 22 - time * 4 + phase) * r * 0.55
                + Math.sin(u * 7 - time * 2 + phase) * r * 0.25;
      const x = xs[i]! + perpx * arc * sign;
      const y = ys[i]! + perpy * arc * sign;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `hsla(${hue} 100% ${lightness}% / 0.85)`;
    ctx.shadowColor = `hsl(${hue} 100% ${Math.min(95, lightness + 5)}%)`;
    ctx.shadowBlur = 10;
    ctx.lineWidth = Math.max(1.2, opts.widthAt(0.5) * 0.35);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * GEOMETRIC — wireframe triangle mesh stretched across the body.
 * Alternating triangles in the snake's own hue at two lightness
 * levels (bright + dim) so the mesh stays monochromatic.
 */
function drawGeometricOverlay(
  ctx: CanvasRenderingContext2D,
  opts: PatternOpts,
  xs: number[],
  ys: number[],
  n: number,
  time: number,
): void {
  const { hue } = opts;
  const breath = 0.85 + 0.15 * Math.sin(time * 1.4);
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 1.1;
  const step = 2;
  for (let i = 0; i < n - step; i += step) {
    const u0 = i / (n - 1);
    const u1 = (i + step) / (n - 1);
    const r0 = opts.widthAt(u0);
    const r1 = opts.widthAt(u1);
    const prev0 = i > 0 ? i - 1 : i;
    const next0 = i + 1;
    const prev1 = i + step - 1;
    const next1 = Math.min(n - 1, i + step + 1);
    const dx0 = xs[next0]! - xs[prev0]!;
    const dy0 = ys[next0]! - ys[prev0]!;
    const dx1 = xs[next1]! - xs[prev1]!;
    const dy1 = ys[next1]! - ys[prev1]!;
    const l0 = Math.hypot(dx0, dy0) || 1;
    const l1 = Math.hypot(dx1, dy1) || 1;
    const px0 = -dy0 / l0, py0 = dx0 / l0;
    const px1 = -dy1 / l1, py1 = dx1 / l1;
    const ax = xs[i]! + px0 * r0 * breath, ay = ys[i]! + py0 * r0 * breath;
    const bx = xs[i]! - px0 * r0 * breath, by = ys[i]! - py0 * r0 * breath;
    const cx = xs[i + step]! + px1 * r1 * breath, cy = ys[i + step]! + py1 * r1 * breath;
    const dxp = xs[i + step]! - px1 * r1 * breath, dyp = ys[i + step]! - py1 * r1 * breath;
    const alt = ((i / step) | 0) % 2 === 0;
    const lightness = alt ? 85 : 60;
    ctx.strokeStyle = `hsla(${hue} 100% ${lightness}% / 0.9)`;
    ctx.shadowColor = `hsl(${hue} 100% ${lightness}%)`;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(cx, cy);
    ctx.lineTo(dxp, dyp);
    ctx.lineTo(bx, by);
    ctx.lineTo(ax, ay);
    ctx.lineTo(dxp, dyp);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * BIO-NEON — bright cell-membrane cracks dividing the body into
 * organic cells. Static crack pattern (per-snake-id seeded) so each
 * snake's cell layout stays recognizable across frames.
 */
function drawBioOverlay(
  ctx: CanvasRenderingContext2D,
  opts: PatternOpts,
  xs: number[],
  ys: number[],
  n: number,
  time: number,
): void {
  const { hue, idSeed } = opts;
  const breath = 0.7 + 0.3 * Math.abs(Math.sin(time * 1.8 + idSeed));
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = `hsla(${hue} 100% 78% / ${0.55 + 0.3 * breath})`;
  ctx.shadowColor = `hsl(${hue} 100% 70%)`;
  ctx.shadowBlur = 8;
  ctx.lineWidth = 1.2;
  const step = 4;
  for (let i = step; i < n - 1; i += step) {
    const u = i / (n - 1);
    const r = opts.widthAt(u);
    const prev = i - 1;
    const next = i + 1;
    const dx = xs[next]! - xs[prev]!;
    const dy = ys[next]! - ys[prev]!;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    ctx.beginPath();
    ctx.moveTo(xs[i]! + px * r * 0.95, ys[i]! + py * r * 0.95);
    ctx.lineTo(xs[i]! - px * r * 0.95, ys[i]! - py * r * 0.95);
    ctx.stroke();
  }
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const u = i / (n - 1);
    const r = opts.widthAt(u);
    const prev = i > 0 ? i - 1 : i;
    const next = i < n - 1 ? i + 1 : i;
    const dx = xs[next]! - xs[prev]!;
    const dy = ys[next]! - ys[prev]!;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    const seed = (i * 2246822519) ^ (idSeed * 3266489917);
    const noise = (((seed >>> 0) & 0xffff) / 0xffff - 0.5) * r * 0.45;
    const x = xs[i]! + px * noise;
    const y = ys[i]! + py * noise;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.restore();
}
